import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateFormTypeMutation, useUpdateFormTypeMutation } from '../../../../hooks/mutations/useFormTypeMutation';
import { FormType } from '../../../../services/apiSLTP/qhse/forms/formTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    formType: FormType | null;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    showEditModal: boolean;
    refetch: () => void;
}

const UpdateFormTypeModal: React.FC<ModalProps> = ({ formType, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(formType?.designation || '');
    const [description, setDescription] = useState(formType?.description || '');
    const [status, setStatus] = useState(formType?.crudStatus || crudStatusEnum.OK);
    const [color, setColor] = useState(formType?.color || 'blue');

    const { mutate: updateFormType } = useUpdateFormTypeMutation({
        onSuccess: () => {
            refetch();
            setShowEditModal(false);
        },
        onError: (error) => console.error('Error updating form type:', error.message),
    });

    const { mutate: createFormType } = useCreateFormTypeMutation({
        onSuccess: () => {
            refetch();
            setShowEditModal(false);
        },
        onError: (error) => console.error('Error creating form type:', error.message),
    });

    const isValidForm = designation.trim() !== '';

    const handleSubmit = () => {
        if (isEditMode) {
            if (!formType) return;
            updateFormType({
                formTypeId: formType._id,
                data: {
                    designation: designation.trim(),
                    description: description.trim(),
                    crudStatus: status,
                    color: color,
                },
            });
        } else {
            createFormType({
                designation: designation.trim(),
                description: description.trim(),
                crudStatus: status,
                color: color,
            });
        }
    };

    return (
        <ModalBody
            title={t('formType')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!isValidForm}
            onSubmit={handleSubmit}
        >
            <CustomTextInput label={t('name')} value={designation} onChange={setDesignation} required />
            <CustomTextInput label={t('description')} value={description} onChange={setDescription} multiline />
            <ToggleLine
                checked={status === crudStatusEnum.OK}
                onChange={(checked) => setStatus(checked ? crudStatusEnum.OK : crudStatusEnum.INACTIVE)}
                title={capitalizeFirstLetter(t('active'))}
            />
            <CustomTextInput label={capitalizeFirstLetter(t('color'))} type="color" value={color} onChange={setColor} />
        </ModalBody>
    );
};

export default UpdateFormTypeModal;
